import { Component, OnInit, Input } from '@angular/core';
import * as moment from 'moment/moment';
import { RestApiService } from '@app/shared/services/rest-api.service';
import { VehiclesService } from '@app/shared/services/vehicles.service';
import { DateService } from '@app/shared/services/date.service';

const DAY_FILTER = 0;
const WEEK_FILTER = 1;
const MONTH_FILTER = 2;

@Component({
  selector: 'app-planning',
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss']
})
export class PlanningComponent implements OnInit {

  @Input() start: string = moment().hours(0).minute(1).format().split('+')[0];
  @Input() end: string = moment().hours(23).minute(59).format().split('+')[0];

  public pickerDate: any;
  public loadingStatus: boolean = true;
  public periodFilter: number = DAY_FILTER;
  public vehicles: Array<any> = [];
  public clock: string = moment().format().split('+')[0].split('T')[1].slice(0, 5);
  public clockOffset: number = 2;
  public selectedVehicle: number = -1;
  public selectedDate: string = '';
  public weekStart: string = '';
  public weekEnd: string = '';

  constructor(
    private vehicleService: VehiclesService,
    private api: RestApiService,
    private dateService: DateService
  ) { }

  ngOnInit() {
    this.selectedDate = moment(this.start).format('DD/MM/YYYY').split('T')[0];
    this.updateReservations(this.start, this.end);
    this.updateClock();
    this.updateWeek();
  }

  shouldClockBeDisplayed(): boolean {
    if (this.periodFilter === DAY_FILTER && moment().format().split('T')[0] === this.start.split('T')[0]) {
      return true;
    }
    if (this.periodFilter === WEEK_FILTER
      && Math.abs(moment(this.weekStart, 'YYYY-MM-DD hh:mm:ss', 'fr').diff(moment(), 'days'))
      + Math.abs(moment(this.weekEnd, 'YYYY-MM-DD hh:mm:ss', 'fr').diff(moment(), 'days')) === 6) {
      return true;
    }
    return false;
  }

  updateClock(): any {
    if (this.periodFilter === DAY_FILTER) {
      this.clockOffset = 2 + (parseInt(this.clock.split(':')[0], 10) - 7);
      this.clockOffset += parseInt(this.clock.split(':')[1], 10) / 60;
    }
    if (this.periodFilter === WEEK_FILTER) {
      this.clockOffset = moment().isoWeekday() * 2;
      this.clockOffset += moment().get('hours') / 12 + (moment().get('minutes') / 60) / 12;
    }
  }

  nextDate(): any {
    if (this.periodFilter === DAY_FILTER) {
      let start = moment(this.start, 'YYYY-MM-DD hh:mm:ss', 'fr');
      let end = moment(this.end, 'YYYY-MM-DD hh:mm:ss', 'fr');

      this.start = start.add(1, 'days').format().split('+')[0];
      this.end = end.add(1, 'days').format().split('+')[0];
      this.updateReservations(this.start, this.end);
    }
    if (this.periodFilter === WEEK_FILTER) {
      let start = moment(this.weekStart, 'YYYY-MM-DD hh:mm:ss', 'fr');
      let end = moment(this.weekEnd, 'YYYY-MM-DD hh:mm:ss', 'fr');

      this.weekStart = start.add(7, 'days').format().split('+')[0];
      this.weekEnd = end.add(7, 'days').format().split('+')[0];
      this.updateReservations(this.weekStart, this.weekEnd);
    }
  }

  prevDate(): any {
    if (this.periodFilter === DAY_FILTER) {
      let start = moment(this.start, 'YYYY-MM-DD hh:mm:ss', 'fr');
      let end = moment(this.end, 'YYYY-MM-DD hh:mm:ss', 'fr');

      this.start = start.subtract(1, 'days').format().split('+')[0];
      this.end = end.subtract(1, 'days').format().split('+')[0];
      this.updateReservations(this.start, this.end);
    }
    if (this.periodFilter === WEEK_FILTER) {
      let start = moment(this.weekStart, 'YYYY-MM-DD hh:mm:ss', 'fr');
      let end = moment(this.weekEnd, 'YYYY-MM-DD hh:mm:ss', 'fr');

      this.weekStart = start.subtract(7, 'days').format().split('+')[0];
      this.weekEnd = end.subtract(7, 'days').format().split('+')[0];
      this.updateReservations(this.weekStart, this.weekEnd);
    }
  }

  zoomToDayView(dayOffset) {
    this.start = moment(this.weekStart).add(dayOffset, 'days').hours(0).minute(1).format().split('+')[0];
    this.end = moment(this.weekStart).add(dayOffset, 'days').hours(23).minute(59).format().split('+')[0];
    this.updateClock();
    this.updateReservations(this.start, this.end);
    this.periodFilter = DAY_FILTER;
  }

  changeDate(newDate): any {
    this.start = moment(newDate, 'DD/MM/YYYY').hours(0).minute(1).format().split('+')[0];
    this.end = moment(newDate, 'DD/MM/YYYY').hours(23).minute(59).format().split('+')[0];
    this.updateClock();
    this.updateWeek();
    if (this.periodFilter === DAY_FILTER) {
      this.updateReservations(this.start, this.end);
    }
    if (this.periodFilter === WEEK_FILTER) {
      this.updateReservations(this.weekStart, this.weekEnd);
    }
  }

  updateWeek(): any {
    let day = moment(this.start.split('T')[0], 'YYYY-MM-DD', 'fr', true).isoWeekday();

    this.weekStart = moment(this.start.split('T')[0], 'YYYY-MM-DD', 'fr', true).subtract(day - 1, 'days').hours(0).minute(1).format().split('+')[0];
    this.weekEnd = moment(this.start.split('T')[0], 'YYYY-MM-DD', 'fr', true).add(7 - day, 'days').hours(23).minute(59).format().split('+')[0];
  }

  changePeriodFilter(filterType): any {
    this.periodFilter = filterType;
    this.updateWeek();
    this.updateClock();
    if (filterType === DAY_FILTER) {
      this.updateReservations(this.start, this.end);
    }
    if (filterType === WEEK_FILTER) {
      this.updateReservations(this.weekStart, this.weekEnd);
    }
  }

  setReservationsInDay(vehicle): any {
    vehicle.reservations.forEach((reservation) => {
      let start = reservation.begin.split('T')[1].split(':');
      let end = reservation.end.split('T')[1].split(':');

      reservation.start = start;
      reservation.end = end;
      reservation.left = 2 + (start[0] - 7);
      reservation.left += start[1] / 60;
      reservation.width = ((end[0] * 3600 + end[1] * 60) - (start[0] * 3600 + start[1] * 60)) / 3600;

      if (reservation.left < 2) {
        let difference = 2 - reservation.left;

        reservation.left = 2;
        reservation.width -= difference;
      }
    });
  }

  setReservationsInWeek(vehicle): any {
    vehicle.reservations.forEach((reservation) => {
      let start = moment(reservation.begin, 'YYYY-MM-DD hh:mm:ss', 'fr');
      let end = moment(reservation.end, 'YYYY-MM-DD hh:mm:ss', 'fr');

      reservation.start = reservation.begin.split('T')[1].split(':');
      reservation.end = reservation.end.split('T')[1].split(':');
      reservation.left = start.isoWeekday() * 2;
      reservation.left += start.get('hours') / 12 + (start.get('minutes') / 60) / 12;
      reservation.width = (Math.abs(end.diff(start, 'minutes')) / 60) / 12;
      if (start.isBefore(moment(this.weekStart)) === true) {
        reservation.left = 2;
      }
      if (reservation.left < 2) {
        let difference = 2 - reservation.left;

        reservation.left = 2;
        reservation.width -= difference;
      }
      // if (end.isAfter(moment(this.weekStart)) === true && end.isBefore(moment(this.weekEnd)) === true) {
      //   reservation.width = reservation.width % 16;
      // }
      if (reservation.width + reservation.left > 16) {
        reservation.width = 16.5 - reservation.left;
      }
    });
  }

  setReservationsInMonth(vehicle) { }

  setReservationsInTimeline(filter): any {
    this.vehicles.forEach((vehicle) => {
      switch (filter) {
        case DAY_FILTER:
          this.setReservationsInDay(vehicle);
          break;
        case WEEK_FILTER:
          this.setReservationsInWeek(vehicle);
          break;
        case MONTH_FILTER:
          this.setReservationsInMonth(vehicle);
          break;
        default:
      };
    });
  }

  updateVehicles(): any {
    let processed = 0;

    if (this.vehicles.length === 0) {
      this.loadingStatus = false;
    }
    this.vehicles.forEach((vehicle, index, array) => {
      this.vehicleService.updateVehicles(1, vehicle.vehicle).then((informations) => {
        if (informations.length === 0) {
          vehicle.informations = [{ id: 0 }];
        } else {
          vehicle.informations = informations;
        }
        processed++;
        if (processed === array.length) {
          this.loadingStatus = false;
        }
      });
    });
  }

  updateReservations(start, end): any {
    this.loadingStatus = true;
    this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetVehiclesReservationsVopV2', {
      dateStart: start,
      dateEnd: end
    }, true).then((res) => {
      if (typeof res === 'object' && res.length > 0) {
        this.vehicles = res;
        this.vehicles.forEach((vehicle) => {
          vehicle.reservations = this.dateService.convertAlltoLocal(vehicle.reservations || []);
        });

        this.updateVehicles();
        this.setReservationsInTimeline(this.periodFilter);
      } else {
        this.loadingStatus = false;
        this.vehicles = [];
      }
    });
  }

}
